import { useState, useEffect, useRef } from "react";
import { Modal, TextInput, Textarea, Select, Switch, LoadingOverlay, Text, Group, FileButton, Box } from "@mantine/core";
import { Button, Alert } from "@xplatform/design-system";
import { IconUpload, IconX } from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import { useDoctype } from "@hooks/flow";
import { useCategory } from "@hooks/useCategory";
import type { DocumentTypeDetailViewModel } from "@models/doctype";
import "./EditDoctypeDialog.scss";

interface EditDoctypeDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    doctype: DocumentTypeDetailViewModel;
}

export const EditDoctypeDialog = ({ open, onClose, onSuccess, doctype }: EditDoctypeDialogProps) => {
    const { t } = useTranslation();
    const { updateDoctype, isLoading } = useDoctype();
    const { categories, fetchCategories } = useCategory();
    const [doctypeName, setDoctypeName] = useState("");
    const [doctypeCode, setDoctypeCode] = useState("");
    const [categoryId, setCategoryId] = useState<string | null>(null);
    const [description, setDescription] = useState("");
    const [status, setStatus] = useState<string | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [mappingEnabled, setMappingEnabled] = useState(false);
    const [mappingService, setMappingService] = useState("");
    const [mappingPluginId, setMappingPluginId] = useState("");
    const [error, setError] = useState<string | null>(null);
    const resetRef = useRef<() => void>(null);

    useEffect(() => {
        if (open) {
            fetchCategories();
            setDoctypeName(doctype.docTypeName);
            setDoctypeCode(doctype.docTypeCode);
            setCategoryId(doctype.categoryId || null);
            setDescription(doctype.description || "");
            setStatus(doctype.status);
            setLogoPreview(doctype.logoUrl || null);
            setLogoFile(null);
            setMappingEnabled(doctype.mappingConfig?.enabled ?? false);
            setMappingService(doctype.mappingConfig?.service ?? "");
            setMappingPluginId(doctype.mappingConfig?.pluginId ?? "");
        }
    }, [open, doctype, fetchCategories]);

    const handleClose = () => {
        setDoctypeName("");
        setDoctypeCode("");
        setCategoryId(null);
        setDescription("");
        setStatus(null);
        setLogoFile(null);
        setLogoPreview(null);
        setMappingEnabled(false);
        setMappingService("");
        setMappingPluginId("");
        setError(null);
        resetRef.current?.();
        onClose();
    };

    const handleSubmit = async () => {
        setError(null);

        if (!doctypeName.trim()) {
            setError("Document type name is required");
            return;
        }

        if (!doctypeCode.trim()) {
            setError("Document type code is required");
            return;
        }

        if (mappingEnabled && (!mappingService.trim() || !mappingPluginId.trim())) {
            setError("Mapping service and plugin ID are required when mapping is enabled");
            return;
        }

        try {
            await updateDoctype(doctype.id, {
                docTypeName: doctypeName,
                docTypeCode: doctypeCode,
                categoryId: categoryId || "",
                description: description || undefined,
                status: status || "",
                logo: logoFile || undefined,
                mappingEnabled,
                mappingService: mappingEnabled ? mappingService.trim() : null,
                mappingPluginId: mappingEnabled ? mappingPluginId.trim() : null,
            });

            if (onSuccess) {
                onSuccess();
            }

            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update document type");
        }
    };

    const handleLogoChange = (file: File | null) => {
        setLogoFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            setLogoPreview(doctype.logoUrl || null);
        }
    };

    const categoryData = [
        { value: "", label: "None" },
        ...categories.map((cat) => ({
            value: cat.id,
            label: cat.name,
        })),
    ];

    const statusData = [
        { value: "active", label: t("admin.table.status.active") },
        { value: "inactive", label: t("admin.table.status.disabled") },
    ];

    return (
        <Modal
            opened={open}
            onClose={handleClose}
            title={`${t("common.edit")} ${t("admin.documentTypes.title")}`}
            size="90%"
            className="edit-doctype-dialog"
            closeButtonProps={{ disabled: isLoading, "aria-label": "Close" }}
            portalProps={{ target: '#admin-portal-root' }}
        >
            <LoadingOverlay visible={isLoading} />

            {error && (
                <Alert color="red" className="edit-doctype-dialog__error" mb="md" icon={<IconX size={16} />}>
                    {error}
                </Alert>
            )}

            <div className="edit-doctype-dialog__form">
                <TextInput
                    label={t("admin.table.columns.name")}
                    value={doctypeName}
                    onChange={(e) => setDoctypeName(e.currentTarget.value)}
                    required
                    autoFocus
                />

                <TextInput
                    label={t("admin.table.columns.code")}
                    value={doctypeCode}
                    onChange={(e) => setDoctypeCode(e.currentTarget.value)}
                    required
                />

                <Select
                    label={t("admin.categories.title")}
                    value={categoryId}
                    onChange={setCategoryId}
                    data={categoryData}
                    searchable
                    comboboxProps={{ withinPortal: false }}
                />

                <Textarea
                    label={t("admin.table.columns.description")}
                    value={description}
                    onChange={(e) => setDescription(e.currentTarget.value)}
                    rows={3}
                />

                <Select
                    label={t("admin.table.columns.status")}
                    value={status}
                    onChange={setStatus}
                    data={statusData}
                    comboboxProps={{ withinPortal: false }}
                />

                <Box>
                    <Switch
                        label={t("admin.documentTypes.mapping.enable", {
                            defaultValue: "Enable mapping",
                        })}
                        description={t("admin.documentTypes.mapping.enableHint", {
                            defaultValue:
                                "Route completed items of this doctype to a mapping service.",
                        })}
                        checked={mappingEnabled}
                        onChange={(e) => setMappingEnabled(e.currentTarget.checked)}
                    />
                    {mappingEnabled && (
                        <Group grow mt="sm">
                            <TextInput
                                label={t("admin.documentTypes.mapping.service", {
                                    defaultValue: "Mapping service",
                                })}
                                placeholder="technical-manual"
                                value={mappingService}
                                onChange={(e) => setMappingService(e.currentTarget.value)}
                                required
                            />
                            <TextInput
                                label={t("admin.documentTypes.mapping.pluginId", {
                                    defaultValue: "Plugin ID",
                                })}
                                placeholder="rmi-topictree-tm-en"
                                value={mappingPluginId}
                                onChange={(e) => setMappingPluginId(e.currentTarget.value)}
                                required
                            />
                        </Group>
                    )}
                </Box>

                <Box>
                    <Text size="sm" fw={500} mb={4}>
                        Logo ({t("admin.documentTypes.labels.optional")})
                    </Text>
                    {logoPreview && (
                        <Box mb="xs">
                            <img
                                src={logoPreview}
                                alt="Logo preview"
                                style={{ maxWidth: 120, maxHeight: 120, objectFit: "contain", borderRadius: 4 }}
                            />
                        </Box>
                    )}
                    <FileButton
                        resetRef={resetRef}
                        onChange={handleLogoChange}
                        accept="image/*"
                    >
                        {(props) => (
                            <Button
                                variant="outline"
                                size="xs"
                                leftSection={<IconUpload size={14} />}
                                {...props}
                            >
                                {logoFile ? logoFile.name : t("admin.documentTypes.actions.uploadLogo")}
                            </Button>
                        )}
                    </FileButton>
                </Box>
            </div>

            <Group justify="flex-end" mt="lg" className="edit-doctype-dialog__actions">
                <Button variant="default" onClick={handleClose} disabled={isLoading}>
                    {t("common.cancel")}
                </Button>
                <Button onClick={handleSubmit} disabled={isLoading}>
                    {t("common.save")}
                </Button>
            </Group>
        </Modal>
    );
};
